import React from "react";

import { useMutation, useQuery } from "@apollo/client";
import { yupResolver } from "@hookform/resolvers/yup";
import { Close as CloseIcon, Error as ErrorIcon } from "@mui/icons-material";
import {
  Box,
  Button,
  IconButton,
  Modal,
  TextField,
  Typography,
} from "@mui/material";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { makeStyles, withStyles } from "tss-react/mui";
import * as yup from "yup";

import ScaleIcon from "@/components/Icons/ScaleIcon";
import { graphql } from "@/gql";
import {
  CreateGoalInput,
  UpdateGoalInput,
  UpdateJournalGoalInput,
} from "@/gql/graphql";
import useGlobalSnackbar from "@/hooks/useGlobalSnackbar";
import { theme } from "@/theme/customTheme";
import { colors } from "@/theme/overrides/colors";
import { formatISODate } from "@/utils/datetime";
import { displayError } from "@/utils/errors";

import { clientWeightLogsPageQuery } from "../ClientWeightLogsPage";

type ModalProps = {
  visibility: boolean;
  onClose: () => void;
  weightGoal: number | null | undefined;
  modalTitle: string;
  goalPresent: boolean;
  title: string;
};
const useStyles = makeStyles()((theme) => ({
  root: {
    overflowY: "auto",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(2),
  },
  modalBody: {
    display: "flex",
    flexDirection: "column",
    margin: theme.spacing(24, 24),
    backgroundColor: colors.white,
    overflowY: "auto",
    width: "470px",
  },
  modalHeader: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    margin: theme.spacing(4, 3, 0, 3),
  },
  modalHeaderText: {
    fontSize: "28px",
    fontWeight: "bold",
  },
  titleText: {
    fontSize: "14px",
    fontWeight: 700,
    color: colors.dark,
    marginBottom: "20px",
  },
  form: {
    margin: theme.spacing(4, 3, 0, 3),
  },
  fields: {
    width: "100%",
  },
  fieldContainer: {
    marginBottom: "20px",
  },
  labelInfo: {
    color: colors.secondary,
    "& .MuiSvgIcon-root": {
      marginRight: "5px",
      marginTop: "4px",
    },
  },
  weightGoal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    background: colors.light,
    padding: theme.spacing(1),
    marginBottom: theme.spacing(4),
  },
  error: {
    fontSize: theme.typography.pxToRem(12),
  },
}));

const FieldLabel = withStyles(Typography, {
  root: {
    color: colors.gray,
    fontSize: "14px",
    marginBottom: "12px",
    "& label": {
      color: "#D13F3F",
    },
  },
});

type FormValues = {
  weight: string;
  title: string;
};

const validationSchema = yup.object().shape({
  weight: yup
    .number()
    .required("Goal weight is required")
    .nullable()
    .transform((value) => (isNaN(value) ? null : value))
    .test(
      "has2decimal",
      "Value must not exceed 2 decimal places",
      function (value) {
        if (!value) return true;
        if (Number.isInteger(value)) return true;
        const result = value.toString().split(".")[1]
          ? value.toString().split(".")[1].length <= 2
          : true;
        return result;
      }
    ),
  title: yup.string().required(),
});

const updateJournalGoalMutation = graphql(`
  mutation UpdateWeightGoal3($updateJournalGoalInput: UpdateJournalGoalInput!) {
    updateJournalGoal(input: $updateJournalGoalInput) {
      journalGoal {
        id
        targetWeightKg
      }
    }
  }
`);

const updateGoalMutation = graphql(`
  mutation UpdateGoal($updateGoalInput: UpdateGoalInput!) {
    updateGoal(input: $updateGoalInput) {
      goal {
        id
        title
      }
    }
  }
`);

const createGoalMutation = graphql(`
  mutation CreateGoal($createGoalInput: CreateGoalInput!) {
    createGoal(input: $createGoalInput) {
      goal {
        id
        title
      }
    }
  }
`);

const EditWeightGoalModal: React.FC<ModalProps> = ({
  visibility,
  onClose,
  weightGoal,
  title,
  modalTitle,
  goalPresent,
}) => {
  const { classes } = useStyles();
  const [updateJournalGoal] = useMutation(updateJournalGoalMutation);
  const [updateGoal] = useMutation(updateGoalMutation);
  const [createGoal] = useMutation(createGoalMutation);
  const { clientId, date } = useParams<{ clientId: string; date: string }>();
  const snackBar = useGlobalSnackbar();
  const { data, loading, refetch } = useQuery(clientWeightLogsPageQuery, {
    variables: { clientId: clientId ?? "", date },
  });
  const goalId = data?.client?.goals.nodes.length
    ? data.client.goals.nodes[0].id
    : "";

  const { control, handleSubmit, reset } = useForm<FormValues>({
    defaultValues: {
      weight: "",
      title: "",
    },
    mode: "onChange",
    reValidateMode: "onBlur",
    resolver: yupResolver(validationSchema),
  });

  React.useEffect(() => {
    reset({ title, weight: weightGoal?.toString() });
  }, [title, weightGoal]);

  const onSubmit: SubmitHandler<FormValues> = async (values) => {
    const formValues = validationSchema.cast(values);
    const updateGoalInput: UpdateGoalInput = {
      clientId: clientId,
      id: goalId,
      patch: {
        title: formValues?.title ?? "",
      },
    };

    const createGoalInput: CreateGoalInput = {
      clientId: clientId,
      patch: {
        title: formValues?.title ?? "",
      },
    };

    try {
      if (goalId == "") {
        await createGoal({
          variables: {
            createGoalInput,
          },
        });
      } else {
        await updateGoal({
          variables: {
            updateGoalInput,
          },
        });
      }

      const updateJournalGoalInput: UpdateJournalGoalInput = {
        clientId: clientId,
        attributes: {
          targetWeightKg: formValues?.weight ?? 0,
        },
      };

      const result = await updateJournalGoal({
        variables: {
          updateJournalGoalInput,
        },
        refetchQueries: [
          {
            query: clientWeightLogsPageQuery,
            variables: {
              clientId: clientId ?? "",
              date: date ?? formatISODate(new Date()),
            },
          },
        ],
      });

      if (result.data) {
        snackBar.open("Goal weight successfully updated");
        onClose();
      }
    } catch (e) {
      snackBar.open(displayError(e, "Error: cannot update weight"));
    }
  };

  return (
    <>
      <Modal open={visibility} onClose={onClose} className={classes.root}>
        <Box className={classes.modalBody}>
          <Box className={classes.modalHeader}>
            <Typography className={classes.modalHeaderText}>
              {modalTitle} Goal Weight
            </Typography>
            <IconButton onClick={onClose} size="large">
              <CloseIcon />
            </IconButton>
          </Box>
          <form
            className={classes.form}
            noValidate
            onSubmit={handleSubmit(onSubmit)}
          >
            <Box>
              {goalPresent && (
                <div className={classes.weightGoal}>
                  <Box display="flex" alignItems="center">
                    <ScaleIcon fontSize="large" />
                    <Box marginRight={theme.spacing(1)} />
                    <Typography variant="body1" fontWeight="bold">
                      Current Goal
                    </Typography>
                  </Box>
                  <Typography variant="h6" fontWeight="bold">
                    {weightGoal} Kg
                  </Typography>
                </div>
              )}
              <Box className={classes.fieldContainer}>
                <FieldLabel>New Goal Weight</FieldLabel>
                <Controller
                  name="weight"
                  control={control}
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <>
                        <TextField
                          {...field}
                          className={classes.fields}
                          type="number"
                          placeholder="E.g. 75 kg"
                        />

                        <Typography
                          variant="subtitle2"
                          color="error"
                          className={classes.error}
                        >
                          {error?.message}
                        </Typography>
                      </>
                    );
                  }}
                />
              </Box>
              <Box className={classes.fieldContainer}>
                <FieldLabel>Goal Description</FieldLabel>
                <Controller
                  name="title"
                  control={control}
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <>
                        <TextField
                          {...field}
                          className={classes.fields}
                          type="string"
                          placeholder="Enter goal description"
                        />
                        <Typography
                          variant="subtitle2"
                          color="error"
                          className={classes.error}
                        >
                          {error?.message}
                        </Typography>
                      </>
                    );
                  }}
                />
                <Typography className={classes.labelInfo}>
                  <ErrorIcon fontSize="small" />
                  The goal will reflect on the profile&apos;s current weight
                  goal
                </Typography>
              </Box>
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                marginBottom={4}
              >
                <Button color="primary" type="submit">
                  Save Change
                </Button>
              </Box>
            </Box>
          </form>
        </Box>
      </Modal>
    </>
  );
};

export default EditWeightGoalModal;
